import { useState } from "react";
import { Alert, App, Button, Card, Form, Input, InputNumber, Modal, Popconfirm, Select, Space, Typography } from "antd";
import { ApiOutlined, CopyOutlined, PlusOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ApiKeyCreatedDto, ApiKeyDto, Role } from "@audit-plan/shared";
import { api, ApiError } from "../../api/client";
import { useSuppliers } from "../../api/suppliers";
import { Pill, fmtDT } from "../pills";
import { ResponsiveList } from "../ResponsiveList";
import { useModalProps } from "../useModalProps";
import { McpSessionsCard } from "../McpSessionsCard";

const ROLES: Role[] = ["viewer", "auditor", "manager", "admin", "supplier"];

async function copy(text: string, ok: () => void, ko: () => void) {
  try {
    await navigator.clipboard.writeText(text);
    ok();
  } catch {
    ko();
  }
}

/** Amministrazione → API: documentazione OpenAPI, MCP per Claude con gli accessi attivi, chiavi di integrazione (Bearer) per i sistemi. */
export function ApiTab() {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const qc = useQueryClient();
  const modalProps = useModalProps(520);
  const sups = useSuppliers();
  const keys = useQuery({ queryKey: ["api-keys"], queryFn: () => api.get<ApiKeyDto[]>("/api-keys") });
  const [form] = Form.useForm();
  const role = Form.useWatch("role", form);
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<ApiKeyCreatedDto | null>(null);
  const err = (e: unknown) => void message.error(e instanceof ApiError ? e.message : t("a_loadFail"));

  const create = useMutation({
    mutationFn: (b: { name: string; role: Role; supplierId?: string | null; expiresInDays?: number | null }) => api.post<ApiKeyCreatedDto>("/api-keys", b),
    onSuccess: (r) => {
      setCreating(false);
      setCreated(r);
      void qc.invalidateQueries({ queryKey: ["api-keys"] });
    },
    onError: err,
  });
  const revoke = useMutation({
    mutationFn: (id: string) => api.del<ApiKeyDto>(`/api-keys/${id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["api-keys"] }),
    onError: err,
  });

  const base = `${window.location.origin}/api/v1`;
  const supName = (id: string | null) => (id ? (sups.data ?? []).find((s) => s.id === id)?.name ?? id : "—");
  const keyStatus = (k: ApiKeyDto) =>
    k.revokedAt ? <Pill tone="bad">{t("api_revoked")}</Pill> : k.expiresAt && k.expiresAt < new Date().toISOString() ? <Pill tone="warn">{t("api_expired")}</Pill> : <Pill tone="ok">{t("api_active")}</Pill>;
  const keyAction = (k: ApiKeyDto) =>
    k.revokedAt ? null : (
      <Popconfirm title={t("api_revokeConfirm")} onConfirm={() => revoke.mutate(k.id)}>
        <Button danger size="small">
          {t("api_revoke")}
        </Button>
      </Popconfirm>
    );
  const mcpUrl = `${window.location.origin}/mcp`;
  const mcpCmd = `claude mcp add --transport http audit-plan ${mcpUrl}`;
  const example = `curl -H "Authorization: Bearer apk_..." ${base}/audits?year=${new Date().getFullYear()}`;

  return (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      <Card title={t("api_docsTitle")}>
        <Space direction="vertical" style={{ width: "100%" }}>
          <Typography.Paragraph style={{ margin: 0 }}>{t("api_docsText")}</Typography.Paragraph>
          <Space wrap>
            <Typography.Text>{t("api_baseUrl")}:</Typography.Text>
            <Typography.Text code copyable style={{ overflowWrap: "anywhere" }}>{base}</Typography.Text>
          </Space>
          <Space wrap>
            <Button icon={<ApiOutlined />} href="/api/docs" target="_blank" rel="noopener">
              {t("api_openDocs")}
            </Button>
            <Button href="/api/docs/json" target="_blank" rel="noopener">
              OpenAPI JSON
            </Button>
          </Space>
          <Typography.Text type="secondary">{t("api_example")}</Typography.Text>
          <Typography.Paragraph code copyable style={{ margin: 0, overflowWrap: "anywhere", whiteSpace: "pre-wrap" }}>
            {example}
          </Typography.Paragraph>
        </Space>
      </Card>

      <Card title={t("api_mcpTitle")}>
        <Space direction="vertical" style={{ width: "100%" }}>
          <Typography.Paragraph style={{ margin: 0 }}>{t("api_mcpText")}</Typography.Paragraph>
          <Space wrap>
            <Typography.Text>{t("api_mcpUrl")}:</Typography.Text>
            <Typography.Text code copyable style={{ overflowWrap: "anywhere" }}>{mcpUrl}</Typography.Text>
          </Space>
          <Typography.Text type="secondary">{t("api_mcpCmd")}</Typography.Text>
          <Typography.Paragraph code copyable style={{ margin: 0, overflowWrap: "anywhere", whiteSpace: "pre-wrap" }}>
            {mcpCmd}
          </Typography.Paragraph>
          <Typography.Text type="secondary">{t("api_mcpLogin")}</Typography.Text>
          <Typography.Text type="secondary">{t("api_mcpDocs")}</Typography.Text>
        </Space>
      </Card>

      <McpSessionsCard scope="all" />

      <Card
        title={t("api_keysTitle")}
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              form.resetFields();
              form.setFieldsValue({ role: "viewer", expiresInDays: 365 });
              setCreating(true);
            }}
          >
            {t("api_newKey")}
          </Button>
        }
      >
        <Alert type="warning" showIcon style={{ marginBottom: 12 }} message={t("api_keysNote")} />
        <Alert type="info" showIcon style={{ marginBottom: 12 }} message={t("api_keysHelp")} />
        <ResponsiveList<ApiKeyDto>
          rowKey="id"
          size="small"
          loading={keys.isLoading}
          dataSource={keys.data ?? []}
          pageSize={false}
          tableProps={{ scroll: { x: true } }}
          card={{
            code: (k) => `${k.prefix}…`,
            title: (k) => k.name,
            subtitle: (k) => `${t(`role_${k.role}`)}${k.supplierId ? ` · ${supName(k.supplierId)}` : ""}`,
            tags: keyStatus,
            fields: (k) => [
              [t("api_lastUsed"), k.lastUsedAt ? fmtDT(k.lastUsedAt) : t("api_never")],
              [t("api_expires"), k.expiresAt ? fmtDT(k.expiresAt) : t("api_noExpiry")],
            ],
            footer: keyAction,
          }}
          columns={[
            { title: t("api_keyName"), dataIndex: "name" },
            { title: t("api_prefix"), dataIndex: "prefix", render: (v: string) => <Typography.Text code>{v}…</Typography.Text> },
            { title: t("role"), dataIndex: "role", render: (r: string, k) => <>{t(`role_${r}`)}{k.supplierId ? ` · ${supName(k.supplierId)}` : ""}</> },
            { title: t("api_created"), dataIndex: "createdAt", render: fmtDT },
            { title: t("api_lastUsed"), dataIndex: "lastUsedAt", render: (v: string | null) => (v ? fmtDT(v) : t("api_never")) },
            { title: t("api_expires"), dataIndex: "expiresAt", render: (v: string | null) => (v ? fmtDT(v) : t("api_noExpiry")) },
            { title: t("status"), render: (_, k) => keyStatus(k) },
            { title: "", align: "right", render: (_, k) => keyAction(k) },
          ]}
        />
      </Card>

      <Modal
        open={creating}
        title={t("api_newKey")}
        {...modalProps}
        onCancel={() => setCreating(false)}
        okText={t("api_create")}
        confirmLoading={create.isPending}
        onOk={() => form.submit()}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" onFinish={(v) => create.mutate({ ...v, supplierId: v.role === "supplier" ? v.supplierId : null, expiresInDays: v.expiresInDays || null })}>
          <Form.Item name="name" label={t("api_keyName")} rules={[{ required: true }]} extra={t("api_nameHint")}>
            <Input maxLength={80} />
          </Form.Item>
          <Form.Item name="role" label={t("role")} rules={[{ required: true }]} extra={t("api_roleHint")}>
            <Select options={ROLES.map((r) => ({ value: r, label: t(`role_${r}`) }))} />
          </Form.Item>
          {role === "supplier" && (
            <Form.Item name="supplierId" label={t("linkedSupplier")} rules={[{ required: true }]}>
              <Select showSearch optionFilterProp="label" options={(sups.data ?? []).map((s) => ({ value: s.id, label: `${s.name} · ${s.code}` }))} />
            </Form.Item>
          )}
          <Form.Item name="expiresInDays" label={t("api_expiresDays")} extra={t("api_expiresHint")}>
            <InputNumber min={1} max={730} style={{ width: 160 }} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal open={!!created} {...modalProps} title={t("api_keyCreated")} onCancel={() => setCreated(null)} footer={<Button onClick={() => setCreated(null)}>{t("close")}</Button>}>
        {created && (
          <Space direction="vertical" style={{ width: "100%" }}>
            <Alert type="warning" showIcon message={t("api_onceOnly")} />
            <div style={{ fontWeight: 600 }}>
              {created.key.name} · {t(`role_${created.key.role}`)}
            </div>
            <Space.Compact style={{ width: "100%" }}>
              <Input readOnly value={created.token} onFocus={(e) => e.target.select()} style={{ fontFamily: "var(--ap-mono)", fontSize: 16 }} />
              <Button
                type="primary"
                icon={<CopyOutlined />}
                onClick={() => copy(created.token, () => void message.success(t("a_copied")), () => void message.warning(t("a_copyManual")))}
              >
                {t("a_copy")}
              </Button>
            </Space.Compact>
            <Typography.Text type="secondary">{t("api_usage")}</Typography.Text>
          </Space>
        )}
      </Modal>
    </Space>
  );
}
